import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowLeft, Bitcoin } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ORDER = ["free", "pro", "team", "studio"];

// Inline ETH glyph (lucide doesn't ship an Ethereum icon).
const EthGlyph = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 256 417" aria-hidden focusable="false">
    <path fill="currentColor" d="M127.961 0l-2.795 9.5v275.668l2.795 2.79 127.962-75.638z" />
    <path fill="currentColor" opacity="0.7" d="M127.962 0L0 212.32l127.962 75.639V154.158z" />
    <path fill="currentColor" d="M127.961 312.187l-1.575 1.92v98.199l1.575 4.6L256 236.587z" />
    <path fill="currentColor" opacity="0.7" d="M127.962 416.905v-104.72L0 236.585z" />
  </svg>
);

const Pricing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null); // { tier, rail }
  const [interval, setInterval] = useState("annual");
  const [chain, setChain] = useState("eth"); // ETH or BTC
  const [enabledChains, setEnabledChains] = useState(["eth", "btc"]);

  const currentTier = user ? (user?.subscription_tier || "free") : null;

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // /billing/status is gated on auth; for anon visitors we just leave the
    // default. For signed-in users we pull the live enabled chains list so
    // we can hide chains whose wallet isn't configured.
    if (!user) return;
    api
      .get("/billing/status")
      .then((r) => {
        const list = r.data?.enabled_chains;
        if (Array.isArray(list) && list.length > 0) {
          setEnabledChains(list);
          if (!list.includes(chain)) setChain(list[0]);
        }
      })
      .catch(() => {});
  }, [user]);

  const subscribe = async (tier) => {
    if (!user) {
      navigate("/");
      return;
    }
    setBusy({ tier, rail: "crypto" });
    try {
      const r = await api.post("/billing/subscribe", { tier, interval, chain });
      navigate(`/checkout/${r.data.sub_invoice_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setBusy(null);
    }
  };

  if (loading || !plans) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-zinc-400 flex items-center justify-center">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pricing-page">
      {!user && (
        <div className="px-8 pt-6">
          <button
            onClick={() => navigate("/")}
            data-testid="pricing-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-500 hover:text-white transition-colors"
          >
            <ArrowLeft size={12} /> Back
          </button>
        </div>
      )}

      <PageHeader
        overline="Plans"
        title="Pricing"
        description="Quill is free forever for getting started. Upgrade with crypto when you outgrow it."
        actions={
          user && currentTier !== "free" ? (
            <button
              onClick={() => navigate("/settings")}
              data-testid="manage-subscription-btn"
              className="px-4 py-2 rounded-md text-sm border border-[#262626] hover:border-white text-zinc-300 hover:text-white inline-flex items-center gap-2"
            >
              Manage subscription
            </button>
          ) : null
        }
      />

      {/* Monthly / Annual + Crypto rail toggle */}
      <div className="px-8 pt-2 pb-2 flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label="Billing interval"
          className="inline-flex border border-[#262626] rounded-md p-0.5 bg-[#0E0E0E]"
          data-testid="billing-interval-toggle"
        >
          {["monthly", "annual"].map((iv) => (
            <button
              key={iv}
              role="tab"
              aria-selected={interval === iv}
              onClick={() => setInterval(iv)}
              data-testid={`interval-${iv}-btn`}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.22em] rounded transition-colors ${
                interval === iv ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              }`}
            >
              {iv === "monthly" ? "Monthly" : "Annual"}
            </button>
          ))}
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.22em] text-emerald-400"
          data-testid="annual-savings-badge"
        >
          Save ~17% · 2 months free
        </span>

        <div
          role="tablist"
          aria-label="Payment crypto"
          className="inline-flex border border-[#262626] rounded-md p-0.5 bg-[#0E0E0E]"
          data-testid="chain-toggle"
        >
          {[
            { id: "eth", label: "Ethereum", Icon: EthGlyph },
            { id: "btc", label: "Bitcoin", Icon: Bitcoin },
          ]
            .filter(({ id }) => enabledChains.includes(id))
            .map(({ id, label, Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={chain === id}
              onClick={() => setChain(id)}
              data-testid={`chain-${id}-btn`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.22em] rounded inline-flex items-center gap-1.5 transition-colors ${
                chain === id ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              }`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>

        <span
          className="text-[10px] uppercase tracking-[0.22em] text-emerald-400/90 inline-flex items-center gap-1.5"
          data-testid="crypto-only-badge"
        >
          <Bitcoin size={11} /> Crypto-only · 100% to your wallet
        </span>
      </div>

      <div className="p-4 md:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl">
        {ORDER.map((key, idx) => {
          const p = plans[key];
          if (!p) return null;
          const isCurrent = currentTier === key;
          const isFree = key === "free";
          const accent = key === "pro";
          const annual = interval === "annual";
          const monthlyPrice = p.price_usd;
          const annualPrice = p.price_usd_annual ?? p.price_usd * 12;
          const displayPerMonth = isFree
            ? 0
            : annual
            ? +(annualPrice / 12).toFixed(2)
            : monthlyPrice;

          const isBusy = busy && busy.tier === key;

          return (
            <motion.section
              key={key}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: idx * 0.08 }}
              data-testid={`plan-card-${key}`}
              className={`relative border p-6 flex flex-col ${
                accent ? "border-white/80 bg-[#0E0E0E]" : "border-[#262626] bg-[#0A0A0A]"
              }`}
            >
              {accent && (
                <div
                  className="absolute -top-2.5 left-6 bg-white text-black text-[10px] uppercase tracking-[0.22em] px-2 py-0.5"
                  data-testid="plan-recommended-badge"
                >
                  Recommended
                </div>
              )}
              {key === "studio" && (
                <div
                  className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-[0.22em] px-2 py-0.5"
                  style={{ background: "hsl(var(--quill-warm))", color: "hsl(var(--quill-ink-warm))" }}
                  data-testid="plan-studio-badge"
                >
                  For $100K+ creators
                </div>
              )}

              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <div className="font-display text-5xl tracking-tighter" data-testid={`plan-price-${key}`}>
                  ${displayPerMonth}
                </div>
                <div className="text-sm text-zinc-500">/mo</div>
              </div>
              {!isFree && annual && (
                <div
                  className="mt-1 text-[10px] uppercase tracking-[0.22em] text-zinc-500"
                  data-testid={`plan-annual-total-${key}`}
                >
                  ${annualPrice} billed yearly · 2 months free
                </div>
              )}
              {!isFree && !annual && (
                <div
                  className="mt-1 text-[10px] uppercase tracking-[0.22em] text-zinc-500"
                  data-testid={`plan-monthly-note-${key}`}
                >
                  Billed monthly
                </div>
              )}

              <ul className="mt-6 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={`${key}-${f}`} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check size={14} className="mt-0.5 text-emerald-400 shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                {isCurrent ? (
                  <div
                    className="w-full text-center py-3 text-xs uppercase tracking-[0.22em] text-zinc-500 border border-[#262626]"
                    data-testid={`plan-current-${key}`}
                  >
                    Current plan
                  </div>
                ) : isFree ? (
                  <div
                    className="w-full text-center py-3 text-xs uppercase tracking-[0.22em] text-zinc-500"
                    data-testid={`plan-free-note`}
                  >
                    No card required
                  </div>
                ) : (
                  <button
                    onClick={() => subscribe(key)}
                    disabled={isBusy}
                    data-testid={`upgrade-${key}-btn`}
                    className={`w-full py-3 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                      accent
                        ? "bg-white text-black hover:bg-zinc-200"
                        : "border border-[#262626] hover:border-white text-zinc-200 hover:text-white"
                    }`}
                  >
                    {isBusy && <Loader2 size={14} className="animate-spin" />}
                    {user
                      ? `Upgrade to ${p.name} · pay in ${chain.toUpperCase()}`
                      : "Sign in to upgrade"}
                  </button>
                )}
              </div>
            </motion.section>
          );
        })}
      </div>

      <div className="px-8 pb-16 max-w-3xl text-xs text-zinc-500 leading-relaxed" data-testid="pricing-footnote">
        Subscriptions are paid in <strong className="text-zinc-300">{chain.toUpperCase()}</strong>{" "}
        directly to the Quill platform wallet — no cards, no processors, no chargebacks.
        The crypto amount is locked at checkout for 1 hour. Your plan unlocks strictly
        after <strong className="text-zinc-300">1 BTC</strong> or{" "}
        <strong className="text-zinc-300">3 ETH</strong> on-chain confirmations. Your
        own client invoices remain direct-wallet, non-custodial.
      </div>
    </div>
  );
};

export default Pricing;
